import { createApp, h } from "vue";
import App from "./App.vue";
// Self-hosted variable fonts (bundled by Vite — no runtime CDN, on-prem safe).
// Fraunces = characterful display serif (opsz/soft/wonk axes); Schibsted
// Grotesk = crisp Nordic UI sans. Imported before the theme so the
// --font-display / --font-stack tokens resolve to loaded faces.
import "@fontsource-variable/fraunces/full.css";
import "@fontsource-variable/schibsted-grotesk/index.css";
import "./styles/reset.css";
import "./styles/theme.css";
import "./styles/effects.css";
import "./styles/app.css";
import "./styles/buttons.css";
import "./styles/tailwind.css";
import PrimeVue from "primevue/config";
import Aura from "@primevue/themes/aura";
import { definePreset } from "@primevue/themes";
import { applyBrand, DEFAULT_BRAND, loadBrand } from "./brand";
import { brandRef } from "./brand-state";
import { brandToPrimevuePreset } from "./theme/brand-to-primevue";
import { installErrorReporting } from "./error-reporting";
import {
  determineAccess,
  gateReason,
  signedInAs,
  signedInName,
} from "./auth/access";
import AccessGate from "./components/AccessGate.vue";

// Resolve the brand BEFORE mounting so the first paint is already themed +
// the `<title>` is correct. Never blocks longer than the fetch — missing /
// malformed brand.json falls back to DEFAULT_BRAND inside loadBrand.
//
// PrimeVue's theme is layered on top of brand.json via
// `brandToPrimevuePreset` — operator brand colours flow into PrimeVue
// components (Dialog, FileUpload, etc.) without touching the legacy CSS
// custom properties that the hand-rolled components still read.
async function boot() {
  // Dev-only brand override: `?brand=/brand-examples/acme.json` swaps the palette
  // without a rebuild, so a proposed identity can be compared against the
  // current one by changing the URL. Gated to `vite dev` — in a production
  // build the query string is ignored entirely, so it cannot be used to
  // restyle the deployed app.
  const brandUrl = import.meta.env.DEV
    ? new URLSearchParams(window.location.search).get("brand") || "/brand.json"
    : "/brand.json";
  const brand = await loadBrand(brandUrl).catch(() => DEFAULT_BRAND);
  applyBrand(brand);
  brandRef.value = brand;

  // Bootstrap the dark-mode toggle from localStorage so the first paint
  // matches the operator's saved preference. The toggle component
  // (header) flips the class + localStorage value at runtime.
  if (typeof localStorage !== "undefined") {
    const saved = localStorage.getItem("choir-theme");
    if (saved === "dark") {
      document.documentElement.classList.add("choir-dark", "dark");
    }
  }

  const preset = definePreset(Aura, brandToPrimevuePreset(brand));

  // Decide access BEFORE creating the app. Anything that mounts starts
  // firing API calls, and acquireToken() redirects to Entra the moment it
  // finds no account — which is exactly the auto-redirect this screen
  // exists to replace. Deciding here is what makes the gate possible at all.
  //
  // `ok` is the overwhelmingly common path and costs one silent token call
  // that the hub would have made anyway.
  // Dev-only preview of the two gate variants: `?gate=signed-out` or
  // `?gate=not-entitled`. Same shape and same reasoning as the `?brand=`
  // override above — a production build ignores the query string entirely,
  // so it can never be used to fake a state on the deployed app.
  const forcedGate = import.meta.env.DEV
    ? new URLSearchParams(window.location.search).get("gate")
    : null;
  const access =
    forcedGate === "signed-out" || forcedGate === "not-entitled"
      ? forcedGate
      : await determineAccess();

  const app = createApp(
    access === "ok"
      ? App
      : {
          render: () =>
            h(AccessGate, {
              state: access,
              signedInAs: signedInAs.value,
              signedInName: signedInName.value,
              reason: gateReason.value,
            }),
        },
  );
  // ADR 0041 — browser errors reach the same log stream as the backend's,
  // joinable on request_id. Installed before mount so a crash during the
  // first render is still reported.
  installErrorReporting(app);
  app.use(PrimeVue, {
    theme: {
      preset,
      options: {
        // PrimeVue swaps into the dark scheme when this CSS selector is
        // present on the page. Tailwind's `darkMode: "class"` watches
        // `.dark` on the same root — we add both to keep them in sync.
        darkModeSelector: ".choir-dark",
      },
    },
  });
  app.mount("#app");
}

void boot();
